/**
 * Toaster — the single viewport for the imperative toast store. Mount once at
 * the app root. Subscribes to `toast-store` and renders two layers:
 *
 *  - message: auto-dismissing notifications (replace window.alert), stacked in
 *    one portal at the top-right, newest at the bottom.
 *  - confirm: a centred modal with Cancel / Confirm that resolves the awaiting
 *    promise (replaces window.confirm).
 *
 * Confirms were top-right cards until MYD-529. A question the user has to
 * answer before anything else happens is a modal, so it now looks like every
 * other popup: centred, the same backdrop as Dialog, rounded like the Chat
 * popup, and closable by clicking outside, Escape, or the X. Every one of those
 * exits answers `false`, exactly like Cancel. No call site changed.
 */

import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { TriangleAlert, X as CloseIcon } from 'lucide-react'
import { cn } from 'lib/utils'
import { Button } from 'components/ui/button'
import PopupCloseButton from 'components/PopupCloseButton'
import { toastVariants, ToastTitle, ToastDescription } from 'components/ui/toast'
import { subscribe, dismiss, answer, getToasts, type ToastItem } from 'components/ui/toast-store'

const MessageCard = ({ item }: { item: ToastItem }) => {
	useEffect(() => {
		if (!item.duration) return
		const timer = setTimeout(() => dismiss(item.id), item.duration)
		return () => clearTimeout(timer)
	}, [item.id, item.duration])

	return (
		<div className={cn(toastVariants({ variant: item.variant }))}>
			<div className="flex-1 min-w-0">
				{item.title && <ToastTitle>{item.title}</ToastTitle>}
				<ToastDescription>{item.message}</ToastDescription>
			</div>
			<button
				type="button"
				onClick={() => dismiss(item.id)}
				aria-label="Dismiss"
				className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-70 transition-opacity hover:text-foreground hover:opacity-100 focus:outline-none"
			>
				<CloseIcon className="h-4 w-4" />
			</button>
		</div>
	)
}

/**
 * One confirm at a time, centred.
 *
 * Above Dialog (z 10002) on purpose: most confirms are raised from inside a
 * dialog ("Discard changes?"), and must sit over it.
 *
 * Escape is caught on `window` in the CAPTURE phase and stopped there. Dialog,
 * Sheet and the Chat popup all listen for Escape on `document`, so without
 * this one keypress would cancel the confirm AND close the dialog under it.
 */
const ConfirmModal = ({ item }: { item: ToastItem }) => {
	const danger = item.variant === 'destructive'
	const cancel = () => answer(item.id, false)

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			answer(item.id, false)
		}
		window.addEventListener('keydown', onKeyDown, true)
		return () => window.removeEventListener('keydown', onKeyDown, true)
	}, [item.id])

	return (
		<div
			role="alertdialog"
			aria-modal="true"
			aria-labelledby={`confirm-title-${item.id}`}
			data-popup-layer=""
			className="fixed inset-0 z-[10003] flex items-center justify-center"
		>
			<div data-testid="confirm-backdrop" className="absolute inset-0 bg-black/50" onClick={cancel} />
			<div className="relative w-full max-w-md mx-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
				<PopupCloseButton onClose={cancel} className="absolute right-1 top-1" />
				<div className="flex items-start gap-3 pr-8">
					<div
						className={cn(
							'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
							danger ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600',
						)}
					>
						<TriangleAlert className="w-5 h-5" />
					</div>
					<div className="flex-1 min-w-0">
						{item.title && <ToastTitle id={`confirm-title-${item.id}`}>{item.title}</ToastTitle>}
						<ToastDescription className="mt-0.5">{item.message}</ToastDescription>
					</div>
				</div>
				<div className="flex justify-end gap-2 mt-4">
					<Button variant="ghost" onClick={cancel}>
						{item.cancelLabel}
					</Button>
					<Button variant={danger ? 'danger' : 'primary'} onClick={() => answer(item.id, true)}>
						{item.confirmLabel}
					</Button>
				</div>
			</div>
		</div>
	)
}

const Toaster = () => {
	const [toasts, setToasts] = useState<ToastItem[]>(getToasts)

	useEffect(() => subscribe(setToasts), [])

	if (toasts.length === 0) return null

	const messages = toasts.filter((t) => t.kind !== 'confirm')
	// Oldest first: a second confirm waits until the first is answered, rather
	// than stacking two modals.
	const confirm = toasts.find((t) => t.kind === 'confirm')

	return ReactDOM.createPortal(
		<>
			{confirm && <ConfirmModal key={confirm.id} item={confirm} />}
			{messages.length > 0 && (
				<div className="pointer-events-none fixed top-4 right-4 z-[10004] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
					{messages.map((item) => (
						<MessageCard key={item.id} item={item} />
					))}
				</div>
			)}
		</>,
		document.body,
	)
}

export default Toaster
